import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';
import { 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Clock, CreditCard, Calendar, Target, Users, Euro
} from "lucide-react";

interface PaymentTrendsProps {
  period: string;
}

const COLORS = {
  primary: '#3b82f6',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#06b6d4',
  gray: '#6b7280'
};

// Datos de ejemplo - en producción vendrían de la base de datos
const paymentTrendsData = [
  { month: 'Ene', onTime: 85, late: 10, overdue: 5, total: 100 },
  { month: 'Feb', onTime: 88, late: 8, overdue: 4, total: 100 },
  { month: 'Mar', onTime: 82, late: 12, overdue: 6, total: 100 },
  { month: 'Abr', onTime: 90, late: 7, overdue: 3, total: 100 },
  { month: 'May', onTime: 87, late: 9, overdue: 4, total: 100 },
  { month: 'Jun', onTime: 89, late: 8, overdue: 3, total: 100 }
];

const paymentMethodsData = [
  { method: 'Efectivo', amount: 1200, percentage: 40, trend: 'up' },
  { method: 'Transferencia', amount: 900, percentage: 30, trend: 'up' },
  { method: 'Tarjeta', amount: 600, percentage: 20, trend: 'down' },
  { method: 'Bizum', amount: 300, percentage: 10, trend: 'up' }
];

const collectionEfficiency = [
  { category: 'Tiempo Promedio de Cobro', value: 15, unit: 'días', trend: 'down', isGood: true },
  { category: 'Tasa de Cobro', value: 94, unit: '%', trend: 'up', isGood: true },
  { category: 'Facturas Vencidas', value: 8, unit: '%', trend: 'down', isGood: true },
  { category: 'Tiempo de Seguimiento', value: 3, unit: 'días', trend: 'down', isGood: true }
];

const debtorAnalysis = [
  { range: '0-30 días', count: 12, amount: 450, color: COLORS.warning },
  { range: '31-60 días', count: 5, amount: 280, color: COLORS.danger },
  { range: '61+ días', count: 2, amount: 150, color: COLORS.gray }
];

export const PaymentTrends = ({ period }: PaymentTrendsProps) => {
  const totalOutstanding = 880;
  const totalDebtors = 19;
  const averageDebtDays = 18;
  const collectionRate = 94.2;

  return (
    <div className="space-y-6">
      {/* KPIs de cobranza */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Tasa de Cobranza
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-success">
                  {collectionRate}%
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-success" />
                  <span className="text-xs text-success">+2.1%</span>
                </div>
              </div>
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pendiente Cobrar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-warning">
                  €{totalOutstanding}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingDown className="w-3 h-3 text-success" />
                  <span className="text-xs text-success">-5.2%</span>
                </div>
              </div>
              <Euro className="w-8 h-8 text-warning" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Deudores Activos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-danger">
                  {totalDebtors}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingDown className="w-3 h-3 text-success" />
                  <span className="text-xs text-success">-3 vs mes</span>
                </div>
              </div>
              <Users className="w-8 h-8 text-danger" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Días Promedio Deuda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-info">
                  {averageDebtDays}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingDown className="w-3 h-3 text-success" />
                  <span className="text-xs text-success">-2 días</span>
                </div>
              </div>
              <Calendar className="w-8 h-8 text-info" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tendencias de pago */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Tendencias de Pago
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={paymentTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="onTime" fill={COLORS.success} name="A Tiempo" />
                <Bar dataKey="late" fill={COLORS.warning} name="Tarde" />
                <Bar dataKey="overdue" fill={COLORS.danger} name="Vencido" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Análisis de deudores */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-warning" />
              Análisis de Deudores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={debtorAnalysis}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="amount"
                  label={({ range, amount }) => `${range}: €${amount}`}
                >
                  {debtorAnalysis.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => `€${value}`} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Métodos de pago */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Métodos de Pago - Análisis de Uso
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {paymentMethodsData.map((method, index) => (
                <div key={method.method} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium">{method.method}</div>
                      <div className="text-sm text-muted-foreground">
                        €{method.amount} ({method.percentage}%)
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {method.trend === 'up' ? (
                      <TrendingUp className="w-4 h-4 text-success" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-danger" />
                    )}
                    <Progress value={method.percentage} className="w-20" />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={paymentMethodsData} layout="horizontal">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="method" type="category" width={100} />
                  <Tooltip formatter={(value) => `€${value}`} />
                  <Bar dataKey="amount" fill={COLORS.primary} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Eficiencia de cobranza */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Eficiencia de Cobranza
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {collectionEfficiency.map((metric, index) => (
              <div key={index} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    {metric.category}
                  </span>
                  {metric.trend === 'up' ? (
                    <TrendingUp className={`w-4 h-4 ${metric.isGood ? 'text-success' : 'text-danger'}`} />
                  ) : (
                    <TrendingDown className={`w-4 h-4 ${metric.isGood ? 'text-success' : 'text-danger'}`} />
                  )}
                </div>
                <div className="text-2xl font-bold">
                  {metric.value}{metric.unit}
                </div>
                <Badge 
                  variant={metric.isGood ? 'default' : 'destructive'}
                  className="mt-2"
                >
                  {metric.isGood ? 'Bueno' : 'Mejorar'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detalle de deudores por rango */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Detalle de Deudores por Antigüedad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Rango</th>
                  <th className="text-center py-2 px-3 font-medium">Deudores</th>
                  <th className="text-right py-2 px-3 font-medium">Importe</th>
                  <th className="text-right py-2 px-3 font-medium">% del Total</th>
                  <th className="text-center py-2 px-3 font-medium">Prioridad</th>
                </tr>
              </thead>
              <tbody>
                {debtorAnalysis.map((range, index) => (
                  <tr key={index} className="border-b hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium">{range.range}</td>
                    <td className="py-2 px-3 text-center">
                      <Badge variant="outline">{range.count}</Badge>
                    </td>
                    <td className="py-2 px-3 text-right font-medium">€{range.amount}</td>
                    <td className="py-2 px-3 text-right">
                      {((range.amount / totalOutstanding) * 100).toFixed(1)}%
                    </td>
                    <td className="py-2 px-3 text-center">
                      <Badge 
                        variant={
                          index === 0 ? 'secondary' : 
                          index === 1 ? 'destructive' : 
                          'outline'
                        }
                      >
                        {index === 0 ? 'Media' : index === 1 ? 'Alta' : 'Crítica'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentTrends;